// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "./common";

// 매니저 모바일 — 본인의 이번 주 일정 (/m/schedule).
// 이전엔 매니저 홈의 "일정" 탭과 "오늘의 방문 → 전체 →"가 /m/timeline(보호자
// 어르신 방문 이력)으로 잘못 연결돼 있었다. 매니저용 일정 화면이 부재한 게 원인
// 이어서 이 컴포넌트를 분리해 만들고 /m/schedule 라우트로 분리한다.
//
// 데이터는 /api/v1/visits?week_start=YYYY-MM-DD 를 재사용. visits_controller의
// scope_for_user 가 manager 역할이면 자동으로 본인 visits만 반환하므로 추가
// 쿼리 필터 없이 그대로 사용한다.

const STATUS_LABEL = { planned: "예정", in_progress: "진행중", completed: "완료", cancelled: "취소" };
const STATUS_COLOR = { planned: "gray", in_progress: "orange", completed: "green", cancelled: "rose" };
const RISK_LABEL  = { critical: "위험", warning: "주의", stable: "안정" };
const RISK_COLOR  = { critical: "rose", warning: "amber", stable: "green" };
const DOW         = ["월","화","수","목","금","토","일"];
const DURATION_OPTIONS = [30, 45, 60, 75, 90, 120];

// 모바일 셸의 공용 chrome. mobile-manager.tsx에 정의된 것과 시각적으로 동일하나,
// import 순환을 피하려고 화면 내부에 가볍게 복제한다 (UI 텍스트만, 동작 없음).
const StatusBar = () => (
  <div style={{display:'flex', justifyContent:'space-between', padding:'8px 22px 4px', fontSize:11, fontWeight:600, color:'var(--ink-1)', flex:'0 0 auto'}}>
    <span>{new Date().toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'})}</span>
    <div style={{display:'flex', gap:5, alignItems:'center'}}>
      <span>●●●</span><span style={{fontSize:10}}>5G</span>
      <span style={{width:18, height:9, border:'1px solid var(--ink-1)', borderRadius:2, position:'relative'}}><span style={{position:'absolute', inset:1, background:'var(--ink-1)'}}/></span>
    </div>
  </div>
);
const HomeBar = () => <div style={{height:4, width:120, background:'var(--ink-1)', borderRadius:99, margin:'2px auto 6px'}}/>;
const TabBar = ({active}) => (
  <div style={{display:'flex', justifyContent:'space-around', padding:'12px 16px 14px', borderTop:'1px solid var(--border-soft)', background:'var(--surface)', flex:'0 0 auto'}}>
    {[
      {l:'홈',    k:'home',     i:'dashboard', href:'/m/home'},
      {l:'일정',  k:'schedule', i:'schedule',  href:'/m/schedule'},
      {l:'지도',  k:'map',      i:'map',       href:'/m/map'},
      {l:'케이스', k:'cases',   i:'case',      href:'/m/cases'},
      {l:'마이',  k:'me',       i:'user',      href:'/m/me'},
    ].map((n,i)=>(
      <a key={i} href={n.href} style={{textAlign:'center', color: n.k===active ? 'var(--orange-600)' : 'var(--ink-3)', textDecoration:'none'}}>
        <span style={{width:22, height:22, display:'inline-block'}}>{Ic[n.i]}</span>
        <div style={{fontSize:9, fontWeight:700, marginTop:2}}>{n.l}</div>
      </a>
    ))}
  </div>
);

function ymd(d) {
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
}
function startOfWeekMon(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // 월=0
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}
function isSameYmd(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function MgrSchedule() {
  const [weekStart, setWeekStart] = React.useState(() => startOfWeekMon(new Date()));
  const [data, setData] = React.useState(null);
  const [err, setErr]   = React.useState(null);
  // 카드 단위 액션 중복 클릭 방지. id 단위로 잠가 한 카드의 처리 중에도 다른 카드는 누를 수 있게 한다.
  const [busyId, setBusyId] = React.useState(null);
  // 상단 KPI 카드(방문/완료/예정) = 상태 빠른필터. null=전체, "completed"=완료만,
  // "upcoming"=예정·진행중. 같은 카드 재클릭/방문 카드=전체로 해제. (KPI 숫자는 전체 기준 유지)
  const [statusFilter, setStatusFilter] = React.useState(null);
  // 방문 일정 추가 바텀시트. 매니저 본인 일정만 추가하므로 매니저 선택 없이
  // /candidates의 actor_care_manager_id로 본인 고정. 백엔드(visits#create)가
  // visit_assignment_allowed?로 타 매니저 배정을 막으므로 UI도 본인만 노출한다.
  const [showCreate, setShowCreate] = React.useState(false);

  const fetchWeek = React.useCallback(async (start) => {
    try {
      const r = await fetch(`/api/v1/visits?week_start=${ymd(start)}`, { credentials: "same-origin" });
      const body = await r.json().catch(() => null);
      if (!r.ok) {
        if (r.status === 401)      setErr("로그인이 필요합니다.");
        else if (r.status === 403) setErr("권한이 없습니다.");
        else                       setErr(apiErrorMessage(body, r.status));
        return;
      }
      setErr(null);
      setData(body);
    } catch (e) {
      setErr(apiErrorMessage(e));
    }
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setData(null);
    (async () => {
      if (cancelled) return;
      await fetchWeek(weekStart);
    })();
    return () => { cancelled = true; };
  }, [weekStart, fetchWeek]);

  // 방문 상세(/m/visit)에서 상태를 바꾸고 뒤로가기로 돌아오면 bfcache 복원이라 재마운트가
  // 없어 카드 배지가 옛 상태로 멈춘다(MgrCaseList와 동일 이슈). pageshow(bfcache 복원 포함)·
  // 탭 재포커스(visibilitychange)에 현재 주를 재조회해 최신화한다. data를 비우지 않아 깜빡임 없음.
  React.useEffect(() => {
    const onShow = () => fetchWeek(weekStart);
    const onVis  = () => { if (document.visibilityState === "visible") fetchWeek(weekStart); };
    window.addEventListener("pageshow", onShow);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("pageshow", onShow);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [weekStart, fetchWeek]);

  // 카드 인라인 체크인/체크아웃. PATCH 후 주간 데이터 재조회로 status·timestamps 반영.
  // 충돌·권한 등은 visits_controller가 401/403/409로 반환하므로 메시지만 노출한다.
  const patchVisit = React.useCallback(async (visitId, body) => {
    if (!visitId || busyId) return;
    setBusyId(visitId);
    setErr(null);
    try {
      const r = await fetch(`/api/v1/visits/${visitId}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ visit: body }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) {
        if (r.status === 401)      setErr("로그인이 필요합니다.");
        else if (r.status === 403) setErr(j?.message || "권한이 없습니다.");
        else                       setErr(apiErrorMessage(j, r.status));
        return;
      }
      await fetchWeek(weekStart);
    } catch (e) {
      setErr(apiErrorMessage(e));
    } finally {
      setBusyId(null);
    }
  }, [busyId, weekStart, fetchWeek]);

  // 체크인/체크아웃은 정산·감사 근거가 되는 타임스탬프를 남기고 상태 전이가
  // 일반 사용자에겐 비가역적이므로(완료→예정은 system_admin만 가능) 오작동
  // 방지용 확인 단계를 둔다. 가벼운 네이티브 confirm로 추가 컴포넌트 없이 처리.
  const visitLabel = (v) => {
    const t = new Date(v.scheduled_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
    return `${v.elderly.name} 어르신 · ${t}`;
  };
  const checkIn = React.useCallback((v) => {
    if (!window.confirm(`${visitLabel(v)} 방문을 체크인하시겠습니까?\n지금 시각으로 기록됩니다.`)) return;
    patchVisit(v.id, { status: "in_progress", check_in_at: new Date().toISOString() });
  }, [patchVisit]);
  const checkOut = React.useCallback((v) => {
    if (!window.confirm(`${visitLabel(v)} 방문을 체크아웃하시겠습니까?\n방문이 완료 처리되고 되돌릴 수 없습니다.`)) return;
    patchVisit(v.id, { status: "completed", check_out_at: new Date().toISOString() });
  }, [patchVisit]);

  // 7일 슬롯 × 해당 요일 visits.
  const byDay = React.useMemo(() => {
    const slots = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      return { date: d, visits: [] };
    });
    const matches = (v) =>
      statusFilter === null ? true
      : statusFilter === "completed" ? v.status === "completed"
      : statusFilter === "upcoming"  ? (v.status === "planned" || v.status === "in_progress")
      : true;
    (data?.visits || []).filter(matches).forEach((v) => {
      const at = new Date(v.scheduled_at);
      const idx = Math.floor((at - weekStart) / (24 * 60 * 60 * 1000));
      if (idx >= 0 && idx < 7) slots[idx].visits.push(v);
    });
    slots.forEach((s) => s.visits.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return slots;
  }, [data, weekStart, statusFilter]);

  const kpis = React.useMemo(() => {
    const visits = data?.visits || [];
    // 취소 방문은 실제 점유시간이 아니므로 시간 합산에서 제외 — 홈(weekly_visit_hours_for)과
    // 같은 정의로 맞춰 두 화면의 "시간" 숫자가 어긋나지 않게 한다.
    const hours = visits
      .filter((v) => v.status !== "cancelled")
      .reduce((sum, v) => sum + (v.duration_mins || 60) / 60, 0);
    const done = visits.filter((v) => v.status === "completed").length;
    const planned = visits.filter((v) => v.status === "planned" || v.status === "in_progress").length;
    return { total: visits.length, hours: Math.round(hours * 10) / 10, done, planned };
  }, [data]);

  const today = new Date();
  const isThisWeek = isSameYmd(weekStart, startOfWeekMon(today));

  const shiftWeek = (delta) => {
    const next = new Date(weekStart);
    next.setDate(next.getDate() + delta * 7);
    setWeekStart(next);
  };

  return (
    <div className="app" style={{background:'var(--bg)', overflow:'hidden'}}>
      <StatusBar/>
      <div style={{padding:'8px 22px 6px', display:'flex', alignItems:'center', gap:8}}>
        <a href="/m/home" aria-label="홈으로"
           style={{width:32, height:32, display:'grid', placeItems:'center', borderRadius:10, background:'var(--surface)', color:'var(--ink-1)', textDecoration:'none'}}>‹</a>
        <div style={{flex:1, fontSize:15, fontWeight:800}}>내 일정</div>
        <button type="button" onClick={() => shiftWeek(-1)} aria-label="지난 주"
                style={{width:30, height:30, border:'none', background:'var(--surface)', borderRadius:8, color:'var(--ink-2)', cursor:'pointer'}}>‹</button>
        <button type="button" onClick={() => setWeekStart(startOfWeekMon(new Date()))} disabled={isThisWeek}
                style={{padding:'0 10px', height:30, border:'none', background: isThisWeek ? 'var(--bg-sub)' : 'var(--orange-50)', borderRadius:8, color: isThisWeek ? 'var(--ink-3)' : 'var(--orange-700)', fontSize:11, fontWeight:700, cursor: isThisWeek ? 'default' : 'pointer'}}>오늘</button>
        <button type="button" onClick={() => shiftWeek(1)} aria-label="다음 주"
                style={{width:30, height:30, border:'none', background:'var(--surface)', borderRadius:8, color:'var(--ink-2)', cursor:'pointer'}}>›</button>
      </div>

      <div style={{padding:'4px 22px 0', display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8}}>
        {[
          { l:"방문", v: kpis.total,   c:"orange", f: null },
          { l:"완료", v: kpis.done,    c:"green",  f: "completed" },
          { l:"예정", v: kpis.planned, c:"sky",    f: "upcoming" },
        ].map((s, i) => {
          const active = statusFilter === s.f;
          return (
            <button key={i} type="button" aria-pressed={active}
                    onClick={() => setStatusFilter(prev => prev === s.f ? null : s.f)}
                    className="card" style={{padding:'10px 12px', textAlign:'left', border:'none', cursor:'pointer',
                      background: active ? `var(--${s.c}-50)` : 'var(--surface)',
                      boxShadow: active ? `inset 0 0 0 1px var(--${s.c}-500)` : undefined}}>
              <div className="muted" style={{fontSize:10, fontWeight:600}}>{s.l}</div>
              <div style={{fontSize:18, fontWeight:800, color: `var(--${s.c}-600)`, marginTop:2, lineHeight:1.1}}>{s.v}</div>
            </button>
          );
        })}
        {(() => {
          // 주간 한도 비교 — 매니저 본인의 max_weekly_hours가 있으면 진척바와 잔여 시간을 노출한다.
          // 100% 초과면 색을 rose로 바꿔 시각적으로 위험을 알리고, 0%면 회색 톤으로 가만히 둔다.
          const maxH = data?.manager?.max_weekly_hours;
          const ratio = maxH ? Math.min(1.25, kpis.hours / maxH) : 0;
          const over = maxH && kpis.hours > maxH;
          const barColor = over ? "var(--rose-500)" : ratio >= 0.85 ? "var(--orange-500)" : "var(--green-500)";
          return (
            <div className="card" style={{padding:'10px 12px'}}>
              <div className="muted" style={{fontSize:10, fontWeight:600}}>시간</div>
              <div style={{fontSize:18, fontWeight:800, color: over ? "var(--rose-500)" : "var(--ink-1)", marginTop:2, lineHeight:1.1}}>
                {kpis.hours}h{maxH ? <span className="muted" style={{fontSize:10, fontWeight:600, marginLeft:2}}>/{maxH}h</span> : null}
              </div>
              {maxH ? (
                <div style={{marginTop:5, height:3, borderRadius:2, background:'var(--bg-sub)', overflow:'hidden'}}>
                  <div style={{width: `${Math.min(100, ratio * 100)}%`, height:'100%', background: barColor, transition:'width .25s'}}/>
                </div>
              ) : null}
            </div>
          );
        })()}
      </div>

      <div style={{padding:'10px 22px 4px', fontSize:10.5, color:'var(--ink-3)', fontWeight:600}}>
        {weekStart.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })} ~ {new Date(weekStart.getTime() + 6 * 86400000).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
      </div>

      {err && <div role="alert" style={{margin:'4px 22px 10px', padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}
      {!err && data === null && <div className="muted" style={{padding:'30px 22px', textAlign:'center', fontSize:12}}>불러오는 중…</div>}

      {!err && data && (
        <div style={{padding:'4px 22px 14px', flex:1, overflow:'auto'}}>
          {byDay.map(({ date, visits }, i) => {
            const isToday = isSameYmd(date, today);
            const isPast  = date < new Date(today.getFullYear(), today.getMonth(), date.getDate() < today.getDate() && date.getMonth() === today.getMonth() ? today.getDate() : 0);
            return (
              <div key={i} style={{display:'flex', gap:12, marginBottom:14}}>
                <div style={{textAlign:'center', minWidth:36, paddingTop:6}}>
                  <div style={{fontSize:11, color: isToday ? 'var(--orange-600)' : 'var(--ink-3)', fontWeight:700}}>{DOW[i]}</div>
                  <div style={{fontSize:22, fontWeight:800, color: isToday ? 'var(--orange-600)' : 'var(--ink-1)', lineHeight:1}}>{date.getDate()}</div>
                </div>
                <div style={{flex:1, minWidth:0}}>
                  {visits.length === 0 && (
                    <div className="muted" style={{fontSize:11, padding:'14px 0'}}>{isToday ? "오늘 예정된 방문 없음" : "—"}</div>
                  )}
                  {visits.map((v) => {
                    const isBusy = busyId === v.id;
                    // 체크인은 오늘 + planned에만, 체크아웃은 in_progress에만 노출.
                    // 카드 자체는 상세 진입 링크라, 액션 버튼은 stopPropagation/preventDefault로 분리한다.
                    const showCheckIn  = isToday && v.status === "planned";
                    const showCheckOut = v.status === "in_progress";
                    const onAction = (handler) => (ev) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      if (isBusy) return;
                      handler(v);
                    };
                    return (
                      <a key={v.id} href={`/m/visit?id=${v.id}`} className="card"
                         style={{display:'flex', alignItems:'center', gap:12, padding:'10px 12px', marginBottom:6, borderLeft:`3px solid var(--${STATUS_COLOR[v.status]==='gray'?'ink-4':STATUS_COLOR[v.status]+'-500'})`, textDecoration:'none', color:'inherit', cursor:'pointer', opacity: isBusy ? 0.6 : 1}}>
                        <div style={{minWidth:44, fontSize:12, fontWeight:800, color: isToday ? 'var(--orange-700)' : 'var(--ink-2)'}}>
                          {new Date(v.scheduled_at).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{fontSize:13, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{v.elderly.name} 어르신</div>
                          <div className="muted" style={{fontSize:10.5, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                            {v.duration_mins || 60}분 · {v.address_text || v.elderly.address}
                          </div>
                        </div>
                        <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4}}>
                          {showCheckIn ? (
                            <button type="button" onClick={onAction(checkIn)} disabled={isBusy}
                                    style={{padding:'4px 10px', border:'none', borderRadius:6, background:'var(--orange-500)', color:'#fff', fontSize:10.5, fontWeight:700, cursor: isBusy ? 'default' : 'pointer'}}>
                              {isBusy ? '...' : '체크인'}
                            </button>
                          ) : showCheckOut ? (
                            <button type="button" onClick={onAction(checkOut)} disabled={isBusy}
                                    style={{padding:'4px 10px', border:'none', borderRadius:6, background:'var(--green-600)', color:'#fff', fontSize:10.5, fontWeight:700, cursor: isBusy ? 'default' : 'pointer'}}>
                              {isBusy ? '...' : '체크아웃'}
                            </button>
                          ) : (
                            <span className={"badge badge-" + STATUS_COLOR[v.status]} style={{fontSize:9}}>{STATUS_LABEL[v.status]}</span>
                          )}
                          {v.elderly.risk_level && v.elderly.risk_level !== "stable" && (
                            <span className={"badge badge-" + RISK_COLOR[v.elderly.risk_level]} style={{fontSize:9}}>{RISK_LABEL[v.elderly.risk_level]}</span>
                          )}
                        </div>
                      </a>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 방문 일정 추가 FAB — 탭바 위에 띄운다. 매니저 본인 일정 추가 진입점. */}
      <button type="button" onClick={() => setShowCreate(true)} aria-label="방문 일정 추가"
              style={{position:'absolute', right:18, bottom:78, width:56, height:56, borderRadius:'50%',
                      border:'none', background:'var(--orange-500)', color:'#fff', cursor:'pointer',
                      display:'grid', placeItems:'center', boxShadow:'0 6px 18px rgba(217,119,6,.4)', zIndex:40,
                      // 전역 `.app svg{width:1em}`(design-tokens.css)가 svg 크기를 font-size로 강제하므로,
                      // FAB의 fontSize로 아이콘 크기를 28px로 키운다(width 속성만으론 덮어써져 13px로 작아짐).
                      fontSize:28, lineHeight:0}}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M12 5v14M5 12h14"/>
        </svg>
      </button>

      {showCreate && (
        <MgrVisitCreateSheet
          defaultAt={defaultCreateAt(weekStart)}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); fetchWeek(weekStart); }}
        />
      )}

      <TabBar active="schedule"/>
      <HomeBar/>
    </div>
  );
}

// 추가 시트 기본 방문 시각: 보고 있는 주가 이번 주면 오늘, 아니면 그 주 월요일.
// 둘 다 09:00로 맞춘다(업무 시작 시각 가정). 로컬 시간 기준으로 구성한다.
function defaultCreateAt(weekStart) {
  const today = new Date();
  const base = isSameYmd(startOfWeekMon(today), weekStart) ? today : new Date(weekStart);
  base.setHours(9, 0, 0, 0);
  return base.toISOString();
}

// 매니저 모바일 방문 추가 바텀시트. 데스크톱 VisitCreateModal(schedule.tsx)의
// 자기-흐름(isSelfFlow) 로직을 모바일 레이아웃으로 옮긴 것. 매니저는 본인에게만
// 배정 가능하므로 매니저 선택 필드 없이 actor_care_manager_id로 고정한다.
//   - /candidates : 내 담당(active 케이스) 어르신 picklist + actor_care_manager_id
//   - POST /visits: 201→onCreated, 409→충돌 인라인, 403/422→에러 인라인
function MgrVisitCreateSheet({ defaultAt, onClose, onCreated }) {
  const [elderlies, setElderlies]   = React.useState([]);
  const [managers, setManagers]     = React.useState([]);
  const [elderlyId, setElderlyId]   = React.useState("");
  const [actorId, setActorId]       = React.useState(null);
  const [scheduledAt, setScheduledAt] = React.useState(defaultAt);
  const [duration, setDuration]     = React.useState(60);
  const [err, setErr]               = React.useState(null);
  const [conflict, setConflict]     = React.useState(null);
  const [loading, setLoading]       = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);

  // Esc 닫기 — 데스크톱 모달과 동작 일치(모바일에선 보조 수단).
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/api/v1/visits/candidates", { credentials: "same-origin" })
      .then(r => r.json().then(j => ({ ok: r.ok, status: r.status, j })))
      .then(({ ok, status, j }) => {
        if (cancelled) return;
        if (!ok) { setErr(status === 403 ? "일정 추가 권한이 없습니다." : apiErrorMessage(j, status)); return; }
        setElderlies(j.elderlies || []);
        setManagers(j.care_managers || []);
        setActorId(j.actor_care_manager_id || null);
        if (!j.can_write) setErr("일정 추가 권한이 없습니다.");
      })
      .catch(e => { if (!cancelled) setErr(apiErrorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const actorManager = actorId != null ? managers.find(m => String(m.id) === String(actorId)) : null;

  // datetime-local 표시값(YYYY-MM-DDTHH:MM). 로컬 분해로 구성한다(toISOString().slice 금지).
  const localValue = (() => {
    const d = new Date(scheduledAt);
    const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  })();

  const submit = async (e) => {
    if (e) e.preventDefault();
    setErr(null); setConflict(null);
    if (!elderlyId) return setErr("어르신을 선택해주세요.");
    if (!actorId)   return setErr("담당 매니저 정보를 불러오지 못했습니다.");
    setSubmitting(true);
    try {
      const r = await fetch("/api/v1/visits", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ visit: {
          care_manager_id: +actorId, elderly_id: +elderlyId,
          scheduled_at: scheduledAt, duration_mins: duration,
        }}),
      });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409) { setConflict({ ...j.conflict, kind: j.kind }); return; }
      if (!r.ok) { setErr(apiErrorMessage(j, r.status)); return; }
      onCreated();
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setSubmitting(false); }
  };

  const noElderly = !loading && !err && elderlies.length === 0;

  return (
    <div onClick={onClose}
         style={{position:'absolute', inset:0, background:'rgba(20,16,12,0.45)', display:'flex', alignItems:'flex-end', zIndex:60}}>
      <form onSubmit={submit} onClick={e=>e.stopPropagation()}
            style={{width:'100%', background:'var(--surface)', borderRadius:'18px 18px 0 0', maxHeight:'88%', overflow:'auto'}}>
        <div style={{padding:'14px 20px 10px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div style={{fontSize:15, fontWeight:800}}>방문 일정 추가</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>
              {actorManager ? <>담당: <b>{actorManager.name}</b> · 내 일정</> : "내 일정"}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="닫기"
                  style={{width:30, height:30, border:'none', background:'var(--bg-sub)', borderRadius:8, color:'var(--ink-2)', cursor:'pointer', display:'grid', placeItems:'center'}}>
            <span style={{width:16, height:16, display:'inline-block'}}>{Ic.close}</span>
          </button>
        </div>

        {err && <div role="alert" style={{margin:'12px 20px 0', padding:'10px 12px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}
        {conflict && (
          <div style={{margin:'12px 20px 0', padding:'10px 12px', background:'var(--amber-50)', color:'#8a6326', borderRadius:10, fontSize:12.5}}>
            ⚠ 같은 {conflict.kind === "elderly" ? "어르신" : "매니저"}의 충돌 일정이 있습니다 — <b>{conflict.elderly_name}</b>
            {conflict.care_manager_name && <> · {conflict.care_manager_name} 매니저</>} · {new Date(conflict.scheduled_at).toLocaleString('ko-KR')} · {conflict.duration_mins}분
            <div style={{fontSize:11.5, marginTop:4}}>
              {conflict.kind === "elderly" ? "시간이나 어르신을 변경해주세요." : "시간이나 매니저를 변경해주세요."}
            </div>
          </div>
        )}

        {loading ? (
          <div className="muted" style={{padding:'28px 20px', textAlign:'center', fontSize:12.5}}>불러오는 중…</div>
        ) : (
          <div style={{padding:'16px 20px', display:'flex', flexDirection:'column', gap:14}}>
            <div>
              <div className="label">어르신 * <span className="muted" style={{fontSize:11, fontWeight:400, marginLeft:4}}>(내 담당만)</span></div>
              <select className="select" value={elderlyId} onChange={e=>setElderlyId(e.target.value)} disabled={noElderly} required>
                <option value="">선택…</option>
                {elderlies.map(e => <option key={e.id} value={e.id}>{e.name} · {e.age}세 · {RISK_LABEL[e.risk_level]||"-"}</option>)}
              </select>
              {noElderly && (
                <div className="muted" style={{fontSize:11, marginTop:6}}>담당 중인 어르신이 없습니다. 권역 운영자에게 케이스 배정을 요청해주세요.</div>
              )}
            </div>

            <div>
              <div className="label">담당 매니저</div>
              <div className="select" style={{display:'flex', alignItems:'center', background:'var(--bg-sub)', color:'var(--ink-2)', cursor:'not-allowed'}}
                   title="본인 일정만 추가할 수 있습니다. 다른 매니저 배정은 권역 운영자에게 요청해주세요.">
                {actorManager ? (<><b style={{color:'var(--ink-1)'}}>{actorManager.name}</b><span style={{marginLeft:6}}>· {actorManager.qualification}</span></>)
                              : <span>본인</span>}
              </div>
            </div>

            <div>
              <div className="label">방문 일시 *</div>
              <input className="input" type="datetime-local" value={localValue} step="900"
                     onChange={e=>setScheduledAt(new Date(e.target.value).toISOString())} required/>
            </div>

            <div>
              <div className="label">소요 시간</div>
              <select className="select" value={duration} onChange={e=>setDuration(+e.target.value)}>
                {DURATION_OPTIONS.map(d => <option key={d} value={d}>{d}분</option>)}
              </select>
            </div>
          </div>
        )}

        <div style={{padding:'12px 20px 18px', borderTop:'1px solid var(--border-soft)', display:'flex', gap:8}}>
          <button type="button" onClick={onClose}
                  style={{flex:'0 0 auto', padding:'12px 18px', border:'none', borderRadius:10, background:'var(--bg-sub)', color:'var(--ink-2)', fontSize:13, fontWeight:700, cursor:'pointer'}}>취소</button>
          <button type="submit" disabled={submitting || loading || noElderly}
                  style={{flex:1, padding:'12px', border:'none', borderRadius:10, background: (submitting||loading||noElderly) ? 'var(--ink-4)' : 'var(--orange-500)', color:'#fff', fontSize:13, fontWeight:800, cursor:(submitting||loading||noElderly)?'default':'pointer'}}>
            {submitting ? '추가 중…' : '일정 추가'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { MgrSchedule };
